import React, { ReactElement, useState, useRef, useEffect } from 'react';

import { Theme, Accordion, AccordionDetails, AccordionSummary, Typography, Container, Avatar, Box, TextField, AccordionActions, Button, ThemeOptions } from '@material-ui/core';
import { createStyles, makeStyles, createMuiTheme } from '@material-ui/core/styles';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

// eslint-disable-next-line no-restricted-imports
import createPalette, { PaletteColor } from '@material-ui/core/styles/createPalette';
import { AlmeTheme, AlmeThemeMode } from '../../../models/AlmeTheme';

const { augmentColor } = createPalette({});

const useStyles = makeStyles((theme: Theme) => createStyles(
    {
        root: {
            width: '60%',
        },
        colorContainer: {
            boxShadow: theme.shadows[1],
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            '&:hover': {
                boxShadow: theme.shadows[4],
            },
        },
        mainPanel: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            height: '204px',
            border: `solid 1px ${theme.palette.divider}`,
            padding: theme.spacing(2),
        },
        augmentContainer: {
            display: 'flex',
        },
        augmentPanel: {
            height: '115px',
            flexGrow: 1,
            padding: theme.spacing(1),
            border: `solid 1px ${theme.palette.divider}`,
        },
        splitPanel: {
            height: '151px',
            border: `solid 1px ${theme.palette.divider}`,
            padding: theme.spacing(2),
        },
        // TODO: add divider color to controls below.
        dividerColor: {
            backgroundColor: theme.palette.divider,
        },
    },
));

function validateColor(color: string) {
    return /^#[0-9A-F]{3,6}$/i.test(color);
}

interface AlmeBasicColorControlProps {
    color: string;
    useColorRef: React.RefObject<HTMLInputElement>;
    title: string;
    idVal: string;
}

function AlmeBasicColorControl(props: AlmeBasicColorControlProps): ReactElement {
    const classes = useStyles();

    const [color, setColor] = useState(props.color);

    useEffect(() => {
        setColor(props.color);
    }, [props.color]);

    const handleColorChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newColor = event.target.value;
        if (newColor?.length && validateColor(newColor)) {
            setColor(newColor);
        }
    };

    const colorStyles = {
        backgroundColor: color,
        color: augmentColor({ main: color }).contrastText,
    };

    return (
        <Box className={`${classes.splitPanel}`} style={colorStyles}>
            <Typography variant='body2'>{props.title}</Typography>
            <TextField
                id={`alme-palette-tool-${props.idVal}`}
                inputRef={props.useColorRef}
                defaultValue={color}
                onChange={handleColorChange}
                inputProps={{
                    style: colorStyles,
                }}
            ></TextField>
        </Box>
    );
}

interface AlmePaletteColorControlProps {
    paletteColor: PaletteColor;
    useMainRef: React.RefObject<HTMLInputElement>;
    useLightRef: React.RefObject<HTMLInputElement>;
    useDarkRef: React.RefObject<HTMLInputElement>;
    title: string;
    idVal: string;
    avatarValue: string;
}

function AlmePaletteColorControl(props: AlmePaletteColorControlProps): ReactElement {
    const classes = useStyles();

    const [main, setMain] = useState(props.paletteColor.main);
    const [light, setLight] = useState(props.paletteColor.light);
    const [dark, setDark] = useState(props.paletteColor.dark);

    useEffect(() => {
        setMain(props.paletteColor.main);
        setLight(props.paletteColor.light);
        setDark(props.paletteColor.dark);
    }, [props.paletteColor]);

    const handleColorChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const primary = event.target.value;
        if (primary?.length && validateColor(primary)) {
            const augmented = augmentColor({ main: primary.toString() });
            setMain(primary);
            setLight(augmented.light);
            setDark(augmented.dark);
        }
    };

    const mainStyles = {
        backgroundColor: main,
        color: augmentColor({ main }).contrastText,
    };

    const lightStyles = {
        backgroundColor: light,
        color: augmentColor({ main: light }).contrastText,
    };

    const darkStyles = {
        backgroundColor: dark,
        color: augmentColor({ main: dark }).contrastText,
    };

    const avatarStyles = {
        backgroundColor: props.paletteColor.contrastText,
        color: props.paletteColor.main,
    };

    return (
        <Container className={classes.colorContainer}>
            <Typography variant='h6'>{props.title}</Typography>
            <Box className={`${classes.mainPanel}`} style={mainStyles}>
                <Avatar style={avatarStyles}>{props.avatarValue}</Avatar>
                <TextField
                    id={`alme-palette-tool-${props.idVal}-main`}
                    inputRef={props.useMainRef}
                    inputProps={{ style: { color: props.paletteColor.contrastText } }}
                    defaultValue={props.paletteColor.main}
                    onChange={handleColorChange}
                ></TextField>
            </Box>
            <Box className={classes.augmentContainer}>
                <Box className={`${classes.augmentPanel}`} style={lightStyles}>
                    <Typography variant='subtitle2'>P - Light</Typography>
                    <TextField
                        id={`alme-palette-tool-${props.idVal}-light`}
                        inputRef={props.useLightRef}
                        inputProps={{ style: { color: props.paletteColor.contrastText } }}
                        value={light}
                        disabled
                    ></TextField>
                </Box>
                <Box className={`${classes.augmentPanel}`} style={darkStyles}>
                    <Typography variant='subtitle2'>P - Dark</Typography>
                    <TextField
                        id={`alme-palette-tool-${props.idVal}-dark`}
                        inputRef={props.useDarkRef}
                        inputProps={{ style: { color: props.paletteColor.contrastText } }}
                        value={dark}
                        disabled
                    ></TextField>
                </Box>
            </Box>
        </Container>
    );
}

function getAppTheme(props: AlmePaletteToolProps): Theme {
    const theme = props.themeMode === 'dark' ? props.theme.darkTheme : props.theme.lightTheme;
    return createMuiTheme(theme);
}

interface AlmePaletteToolProps {
    theme: AlmeTheme;
    themeMode: AlmeThemeMode;
    setTheme: (theme: AlmeTheme) => void;
}

export default function AlmePaletteTool(props: AlmePaletteToolProps): ReactElement {
    const classes = useStyles();

    const appTheme = getAppTheme(props);

    const useMainPrimaryRef = useRef<HTMLInputElement>(null);
    const useLightPrimaryRef = useRef<HTMLInputElement>(null);
    const useDarkPrimaryRef = useRef<HTMLInputElement>(null);
    const useMainSecondaryRef = useRef<HTMLInputElement>(null);
    const useLightSecondaryRef = useRef<HTMLInputElement>(null);
    const useDarkSecondaryRef = useRef<HTMLInputElement>(null);
    const useDefaultRef = useRef<HTMLInputElement>(null);
    const usePaperRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        useMainPrimaryRef.current && (useMainPrimaryRef.current.value = appTheme.palette.primary.main);
        useLightPrimaryRef.current && (useLightPrimaryRef.current.value = appTheme.palette.primary.light);
        useDarkPrimaryRef.current && (useDarkPrimaryRef.current.value = appTheme.palette.primary.dark);

        useMainSecondaryRef.current && (useMainSecondaryRef.current.value = appTheme.palette.secondary.main);
        useLightSecondaryRef.current && (useLightSecondaryRef.current.value = appTheme.palette.secondary.light);
        useDarkSecondaryRef.current && (useDarkSecondaryRef.current.value = appTheme.palette.secondary.dark);

        useDefaultRef.current && (useDefaultRef.current.value = appTheme.palette.background.default);
        usePaperRef.current && (usePaperRef.current.value = appTheme.palette.background.paper);
    }, [
        appTheme.palette.background.default, appTheme.palette.background.paper,
        appTheme.palette.primary.dark, appTheme.palette.primary.light, appTheme.palette.primary.main,
        appTheme.palette.secondary.dark, appTheme.palette.secondary.light, appTheme.palette.secondary.main,
    ]);

    const applyThemeClick = () => {
        const customTheme: ThemeOptions = {
            ...appTheme,
            palette: {
                ...appTheme.palette,
                type: props.themeMode === 'dark' ? 'dark' : 'light' as const,
                primary: {
                    ...appTheme.palette.primary,
                    main: useMainPrimaryRef.current?.value || appTheme.palette.primary.main,
                    light: useLightPrimaryRef.current?.value || appTheme.palette.primary.light,
                    dark: useDarkPrimaryRef.current?.value || appTheme.palette.primary.dark,
                },
                secondary: {
                    ...appTheme.palette.secondary,
                    main: useMainSecondaryRef.current?.value || appTheme.palette.secondary.main,
                    light: useLightSecondaryRef.current?.value || appTheme.palette.secondary.light,
                    dark: useDarkSecondaryRef.current?.value || appTheme.palette.secondary.dark,
                },
                background: {
                    ...appTheme.palette.background,
                    default: useDefaultRef.current?.value || appTheme.palette.background.default,
                    paper: usePaperRef.current?.value || appTheme.palette.background.paper,
                },
            },
        };
        const customMuiTheme = {
            lightTheme: customTheme.palette?.type === 'light' ? customTheme : props.theme.lightTheme,
            darkTheme: customTheme.palette?.type === 'dark' ? customTheme : props.theme.darkTheme,
        } as AlmeTheme;

        props.setTheme(customMuiTheme);
    };

    return (
        <Box className={classes.root}>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel-color-tool-content"
                    id="panel-color-tool-header"
                >
                    <Typography variant='h6'>Color Tool</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <AlmePaletteColorControl
                        title='Primary'
                        idVal='primary'
                        avatarValue="P"
                        paletteColor={appTheme.palette.primary}
                        useMainRef={useMainPrimaryRef}
                        useLightRef={useLightPrimaryRef}
                        useDarkRef={useDarkPrimaryRef}
                    />
                    <AlmePaletteColorControl
                        title='Secondary'
                        idVal='secondary'
                        avatarValue="S"
                        paletteColor={appTheme.palette.secondary}
                        useMainRef={useMainSecondaryRef}
                        useLightRef={useLightSecondaryRef}
                        useDarkRef={useDarkSecondaryRef}
                    />
                    <Container className={classes.colorContainer}>
                        <Typography variant='h6'>Background</Typography>
                        <AlmeBasicColorControl
                            title="Default"
                            idVal='background-default'
                            useColorRef={useDefaultRef}
                            color={appTheme.palette.background.default}
                        />
                        <AlmeBasicColorControl
                            title="Paper"
                            idVal='background-paper'
                            useColorRef={usePaperRef}
                            color={appTheme.palette.background.paper}
                        />
                    </Container>
                </AccordionDetails>
                <AccordionActions>
                    <Button color='primary' variant='contained' onClick={applyThemeClick}>Apply Theme</Button>
                </AccordionActions>
            </Accordion>
        </Box>
    );
}